// Curated, factual content celebrating Haitian culture, history & heritage.
// Written with care and accuracy. Sources: UNESCO, Haitian government,
// historical record. Open to community edits.

export const CULTURE_TOPICS = [
  {
    slug: "history",
    icon: "Landmark",
    titleKreyol: "Istwa Ayiti",
    title: "History",
    blurb:
      "From the first free Black republic to today — the story Haiti gave the world.",
    accent: "#D21034", // Haitian flag red
    image:
      "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=1600&q=80",
    intro:
      "Haiti is the only nation in history born from a successful slave-led revolution. On January 1, 1804, after 13 years of war, Haitian generals declared independence at Gonaïves — making Ayiti the first free Black republic in the world and the second republic in the Americas after the United States.",
    sections: [
      {
        heading: "The Revolution (1791 – 1804)",
        body:
          "On August 14, 1791, the Bois Caïman ceremony lit the spark of the largest slave uprising in history. Toussaint Louverture rose as the brilliant general who unified the rebellion. After his betrayal and death in a French prison, Jean-Jacques Dessalines completed the fight — defeating Napoleon's army at the Battle of Vertières on November 18, 1803.",
      },
      {
        heading: "January 1, 1804 — Independence",
        body:
          "Dessalines proclaimed Haiti's independence at Gonaïves. The new nation took back the indigenous Taíno name 'Ayiti' (Land of High Mountains) — a symbolic erasure of the colonial name 'Saint-Domingue.' Catherine Flon stitched the first flag by tearing the white band out of the French tricolore.",
      },
      {
        heading: "Figures Who Shaped Us",
        body:
          "Toussaint Louverture · Jean-Jacques Dessalines · Henri Christophe · Alexandre Pétion · Catherine Flon · Sanité Bélair · Cécile Fatiman · Capois La Mort · Boukman Dutty.",
      },
      {
        heading: "Modern Era",
        body:
          "From the indemnity debt forced on Haiti by France (1825 – 1947) to the rebuild after the 2010 earthquake, Haitians have continued to define resilience. The diaspora — over 4 million strong across the US, Canada, France, the Dominican Republic, Chile and beyond — carries the culture worldwide.",
      },
    ],
    didYouKnow:
      "The French extorted 150 million gold francs from Haiti in 1825 as 'reparations' for the lost slaves. It was the only time in modern history that the victors of a war of liberation paid reparations to the defeated. Haiti finished paying in 1947.",
  },
  {
    slug: "geography",
    icon: "Mountain",
    titleKreyol: "Jewografi",
    title: "Geography",
    blurb:
      "Mountains, beaches, and the warm Caribbean — the lay of the land.",
    accent: "#00209F", // Haitian flag blue
    image:
      "https://images.unsplash.com/photo-1517867065570-6e69e98a8e75?auto=format&fit=crop&w=1600&q=80",
    intro:
      "Haiti occupies the western third of Hispaniola, the second-largest island in the Caribbean. 'Ayiti' literally means 'Land of High Mountains' in Taíno — and it lives up to the name: roughly three-quarters of the country is mountainous.",
    sections: [
      {
        heading: "The 10 Departments",
        body:
          "Artibonite · Centre · Grand'Anse · Nippes · Nord · Nord-Est · Nord-Ouest · Ouest · Sud · Sud-Est. The capital Port-au-Prince sits in Ouest, while Cap-Haïtien — the historic capital — anchors the Nord.",
      },
      {
        heading: "Landmarks",
        body:
          "Citadelle Laferrière (UNESCO World Heritage, the largest fortress in the Americas) · Sans-Souci Palace · Bassin Bleu · Saut-d'Eau waterfalls · Île-à-Vache · Jacmel's historic centre · Pic Macaya National Park.",
      },
      {
        heading: "Climate & Sea",
        body:
          "Tropical year-round, with two rainy seasons (April–June and October–November). 1,771 km of coastline and a deep, turquoise Caribbean Sea full of reefs and fish. Labadee, Île-à-Vache, and Port-Salut offer beaches that rival any in the region.",
      },
    ],
    didYouKnow:
      "Pic la Selle (2,680 m / 8,793 ft) is Haiti's highest peak — and one of the tallest mountains in the Caribbean.",
  },
  {
    slug: "carnival",
    icon: "Drum",
    titleKreyol: "Kanaval",
    title: "Carnival & Festivals",
    blurb: "Rara, Kanaval, Fèt Gede — when the whole country dances.",
    accent: "#F2B400",
    image:
      "https://images.unsplash.com/photo-1547499942-9d3da82753ad?auto=format&fit=crop&w=1600&q=80",
    intro:
      "No country celebrates like Haiti. Carnival season runs from January through Mardi Gras, with rara following through Easter — a months-long open-air festival of music, costume, satire and ancestral spirit.",
    sections: [
      {
        heading: "Kanaval (Carnival)",
        body:
          "Three days before Ash Wednesday, cities transform. Float-mounted sound systems play original kanaval songs commissioned for the year — kompa and rasin bands compete to be 'meringue of the year.' Jacmel's carnival, with its hand-crafted papier-mâché masks, is UNESCO-recognized.",
      },
      {
        heading: "Rara",
        body:
          "From Ash Wednesday until Easter, neighbourhood rara bands walk for kilometres playing bamboo trumpets (vaksin), drums and cymbals. Rara is street ritual, social commentary and pilgrimage all at once — born in the Vodou tradition.",
      },
      {
        heading: "Fèt Gede (Nov 1 – 2)",
        body:
          "Haiti's Day of the Dead. Faces painted in black, white and purple, families honour their ancestors at cemeteries with rum, coffee, songs and the spirit of Baron Samedi and Manman Brigitte.",
      },
    ],
    didYouKnow:
      "Jacmel Carnival's papier-mâché masks — animals, demons, political caricatures — are the work of mask-makers who train apprentices for years. Each mask is made for one parade and burned afterward.",
  },
  {
    slug: "cuisine",
    icon: "UtensilsCrossed",
    titleKreyol: "Manje Ayisyen",
    title: "Cuisine",
    blurb: "Griot, pikliz, joumou — flavours that tell the story of freedom.",
    accent: "#D21034",
    image:
      "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1600&q=80",
    intro:
      "Haitian cuisine is bold, slow-cooked, and deeply symbolic. Built on African, French, Taíno and Spanish foundations — and seasoned with the famous 'epis' marinade of peppers, scallions, garlic, parsley and Scotch bonnet.",
    sections: [
      {
        heading: "Soup Joumou — the soup of freedom",
        body:
          "Traditionally forbidden to enslaved Africans by French colonists, soup joumou (squash soup with beef, vegetables, pasta) was eaten by Haitians for the first time as free people on January 1, 1804. Every Haitian household eats it on Independence Day. UNESCO inscribed it on the Intangible Cultural Heritage list in December 2021.",
      },
      {
        heading: "Signature Dishes",
        body:
          "Griot (fried marinated pork) · Diri ak djon djon (rice with black mushrooms unique to northern Haiti) · Tchaka · Lambi (conch) · Bouillon · Akra (grated malanga fritters) · Pen patat (sweet potato bread) · Mayi moulen ak sòs pwa.",
      },
      {
        heading: "Pikliz — the national condiment",
        body:
          "A spicy slaw of cabbage, carrots, Scotch bonnet, lime and vinegar that sits on the table beside every fried plantain (banann peze) and every plate of griot.",
      },
      {
        heading: "Drinks",
        body:
          "Kremas (rum + coconut cream + condensed milk + spices) at every wedding and Christmas. Clairin (artisanal cane spirit, lately celebrated by international spirit critics). Akasan (warm corn-and-cinnamon drink) for breakfast.",
      },
    ],
    didYouKnow:
      "Haiti became the first country in the world to have a national dish formally recognized as UNESCO Intangible Heritage — joumou soup, in December 2021.",
  },
  {
    slug: "arts",
    icon: "Palette",
    titleKreyol: "Atizay",
    title: "Arts & Crafts",
    blurb:
      "Vivid paintings, ironwork sculpture, papier-mâché — art that vibrates.",
    accent: "#00209F",
    image:
      "https://images.unsplash.com/photo-1547826039-bfc35e0f1ea8?auto=format&fit=crop&w=1600&q=80",
    intro:
      "Haitian visual art exploded onto the world stage in the 1940s with the Centre d'Art in Port-au-Prince. Saint-Soleil, naive, Vodou-inspired, surrealist — Haitian painters are collected by MoMA, the Smithsonian, and the Vatican.",
    sections: [
      {
        heading: "Painting",
        body:
          "Hector Hyppolite, Philomé Obin, Wilson Bigaud, Préfète Duffaut, Edouard Duval-Carrié, Mario Benjamin. Bright colour, dense composition, Vodou iconography. 'Naive' is a misnomer — these are deeply intentional works.",
      },
      {
        heading: "Iron Sculpture (Fer Découpé)",
        body:
          "Started in Croix-des-Bouquets by Georges Liautaud in the 1950s. Sculptors flatten oil drums and hammer them into intricate cut-iron pieces — mermaids, lwa, trees of life — sold from Port-au-Prince to Brooklyn.",
      },
      {
        heading: "Papier-Mâché & Textiles",
        body:
          "Jacmel's mask-makers and the embroidered drapo Vodou (sequined flags) — bright satin banners depicting lwa, hand-stitched with thousands of glass beads. Each takes weeks.",
      },
    ],
    didYouKnow:
      "Saint-Soleil, the artistic movement founded by Maud Robart and Tiga in the 1970s, is considered by André Malraux one of the most original art movements of the 20th century.",
  },
  {
    slug: "music",
    icon: "Music",
    titleKreyol: "Mizik Ayisyen",
    title: "Music & Dance",
    blurb:
      "Kompa, Rasin, Rara, Twoubadou — Haiti gave the Caribbean its groove.",
    accent: "#F2B400",
    image:
      "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1600&q=80",
    intro:
      "Haiti's musical output has shaped the entire Caribbean — kompa (compas direct) influenced zouk, Dominican bachata, and Antillean cadence-lypso. Rasin music revived Vodou drumming for global stages.",
    sections: [
      {
        heading: "Kompa (Konpa Direk)",
        body:
          "Invented by Nemours Jean-Baptiste in 1955. Smooth meringue groove anchored by the bass and the tcha-tcha. Tabou Combo, Sweet Micky, Carimi, Klass, Harmonik, T-Vice — kompa is Haiti's heartbeat.",
      },
      {
        heading: "Rasin (Roots)",
        body:
          "Rooted in Vodou drumming and Petwo / Rada rhythms. Boukman Eksperyans, RAM, Boukan Ginen, Azor — political, ancestral, electrifying. Carnival 2026 will see new rasin anthems again.",
      },
      {
        heading: "Twoubadou & Rabòday",
        body:
          "Twoubadou is the romantic acoustic Haitian sound — guitar, accordion, percussion. Rabòday is the new, raw, sample-heavy Port-au-Prince street sound that's gone viral on TikTok.",
      },
      {
        heading: "Legends to Hear First",
        body:
          "Tabou Combo · Boukman Eksperyans · Wyclef Jean · Coupé Cloué · Tropicana d'Haïti · Carimi · BélO · Mikaben · J. Perry · Vayb · Rutshelle Guillaume · Paul Beaubrun.",
      },
    ],
    didYouKnow:
      "Wyclef Jean — born in Croix-des-Bouquets — co-founded The Fugees, sold over 22 million albums, and continues to write Carnival anthems for Haiti every year.",
  },
];

export const QUICK_FACTS = [
  { label: "Independence", value: "1 January 1804" },
  { label: "Capital", value: "Port-au-Prince" },
  { label: "Languages", value: "Kreyòl · Français" },
  { label: "Population", value: "~11.7 million" },
  { label: "Diaspora", value: "~4 million worldwide" },
  { label: "Currency", value: "Gourde (HTG)" },
  { label: "National motto", value: "L'union fait la force" },
  { label: "Flag adopted", value: "18 May 1803" },
];

export const HERO_QUOTES = [
  { kreyol: "L'union fait la force.", english: "Unity makes strength." },
  { kreyol: "Ayiti pa janm mouri.", english: "Haiti never dies." },
  { kreyol: "Lè w bezwen anyen, mande Bondye.", english: "When you need anything, ask God." },
  { kreyol: "Piti piti zwazo fè nich li.", english: "Little by little, the bird builds its nest." },
];
